import { entityCardTypeLabelKeys, getEntityCardHref, getEntityLoreHref, type EntityCardType } from "@/types/entity";
import {
  loreCanonStatusLabels,
  loreEntryTypeLabels,
  type LoreCanonStatus,
  type LoreEntryType,
} from "@/types/lore";
import type { SubscriptionTier } from "@/types/projects";

export type AffinityChartNodeFamily = "entity-card" | "lore-entry";
export type AffinityChartRecordKind = AffinityChartNodeFamily;
export type AffinityChartFilterType = EntityCardType | LoreEntryType;
export type AffinityChartRelationType = "entity_entity" | "entity_lore" | "lore_lore" | string;

export type AffinityChartPosition = {
  x: number;
  y: number;
};

export type AffinityChartLayoutPositions = Record<string, AffinityChartPosition>;

export type AffinityChartNode = {
  canonStatus: LoreCanonStatus | null;
  canonStatusLabelKey: string | null;
  family: AffinityChartNodeFamily;
  href: string;
  id: string;
  imageUrl: string | null;
  label: string;
  recordId: string;
  recordKind: AffinityChartRecordKind;
  summaryText: string;
  type: AffinityChartFilterType;
  typeLabelKey: string;
};

export type AffinityChartEdge = {
  id: string;
  label: string;
  relationType: AffinityChartRelationType;
  relationTypeLabelKey: string;
  sourceLabel: string;
  sourceNodeId: string;
  targetLabel: string;
  targetNodeId: string;
};

export type AffinityChartFilterOption = {
  count: number;
  family: AffinityChartNodeFamily;
  labelKey: string;
  type: AffinityChartFilterType;
};

export type AffinityChartSnapshot = {
  counts: {
    edges: number;
    entityNodes: number;
    loreNodes: number;
  };
  edges: AffinityChartEdge[];
  filterOptions: AffinityChartFilterOption[];
  generatedAt: number;
  layoutPositions: AffinityChartLayoutPositions;
  nodes: AffinityChartNode[];
  projectId: string;
};

export type AffinityChartFilters = {
  visibleTypes: AffinityChartFilterType[];
};

export type AffinityChartSelection =
  | {
      kind: "edge";
      selectedId: string;
    }
  | {
      kind: "node";
      selectedId: string;
    }
  | {
      kind: "none";
      selectedId: null;
    };

export type AffinityChartVisibleGraph = {
  counts: {
    visibleEdges: number;
    visibleNodes: number;
  };
  edges: AffinityChartEdge[];
  nodes: AffinityChartNode[];
};

export type AffinityChartDetailView =
  | {
      descriptionKey: string;
      kind: "empty";
      titleKey: string;
    }
  | {
      kind: "edge";
      edge: AffinityChartEdge;
      sourceNode: AffinityChartNode;
      targetNode: AffinityChartNode;
    }
  | {
      kind: "node";
      node: AffinityChartNode;
    };

export type AffinityChartAccessState = {
  recommendedTier: SubscriptionTier | null;
  state: "idle" | "available" | "locked";
  tier: SubscriptionTier | null;
};

export function createAffinityChartNodeId(kind: AffinityChartRecordKind, recordId: string) {
  return `${kind}:${recordId}`;
}

export function createEmptyAffinityChartFilters(): AffinityChartFilters {
  return {
    visibleTypes: [],
  };
}

export function createEmptyAffinityChartSelection(): AffinityChartSelection {
  return {
    kind: "none",
    selectedId: null,
  };
}

export function buildDefaultAffinityChartFilters(
  filterOptions: AffinityChartFilterOption[],
): AffinityChartFilters {
  return {
    visibleTypes: filterOptions.map((option) => option.type),
  };
}

export function hasActiveAffinityChartFilters(
  filters: AffinityChartFilters,
  filterOptions: AffinityChartFilterOption[],
) {
  return filters.visibleTypes.length !== filterOptions.length;
}

export function getAffinityChartNodeHref(
  projectId: string,
  kind: AffinityChartRecordKind,
  recordId: string,
) {
  return kind === "entity-card"
    ? getEntityCardHref(projectId, recordId)
    : getEntityLoreHref(projectId, recordId);
}

export function getAffinityChartTypeLabelKey(type: AffinityChartFilterType) {
  if (type in entityCardTypeLabelKeys) {
    return entityCardTypeLabelKeys[type as EntityCardType];
  }

  return loreEntryTypeLabels[type as LoreEntryType];
}

export function getAffinityChartCanonStatusLabelKey(status: LoreCanonStatus | null) {
  return status ? loreCanonStatusLabels[status] : null;
}

export function getAffinityChartRelationTypeLabelKey(type: AffinityChartRelationType) {
  switch (type) {
    case "entity_entity":
      return "affinityChart.relationTypes.entityEntity";
    case "entity_lore":
      return "affinityChart.relationTypes.entityLore";
    case "lore_lore":
      return "affinityChart.relationTypes.loreLore";
    default:
      return "affinityChart.relationTypes.generic";
  }
}
